import { NOTES, normalizeKey } from '../../data/theoryConstants.js';

/**
 * CadenceDetector.js
 * Analyzes harmonic transitions to identify cadential movement.
 * Used to tag instructions with 'Resolve', 'Deceptive', etc.
 */

const getInterval = (noteA, noteB) => {
    return (noteB - noteA + 12) % 12;
};

/**
 * Detects the type of cadence between two chords.
 * @param {Object} prevChord - { rootPitchClass, quality, key }
 * @param {Object} currChord - { rootPitchClass, quality, key }
 * @returns {string} - 'perfect', 'plagal', 'half', 'deceptive', 'none'
 */
export const detectCadence = (prevChord, currChord) => {
    if (!prevChord || !currChord) return 'none';

    // Ensure rootPitchClass exists
    if (prevChord.rootPitchClass === undefined || currChord.rootPitchClass === undefined) return 'none';

    const interval = getInterval(prevChord.rootPitchClass, currChord.rootPitchClass);
    const prevQ = prevChord.quality || 'major';
    const currQ = currChord.quality || 'major';

    // Context Check: Are we in the same key?
    // If keys differ, it's a modulation, not a standard cadence (usually).
    // But V/newKey -> I/newKey is valid.

    // --- AUTHENTIC CADENCE (V -> I) ---
    // Root Movement: Up 4th (5 semitones) or Down 5th
    // G (7) -> C (0) = 5 semitones
    if (interval === 5) {
        if (prevQ === 'major' || prevQ === 'dom7') {
            if (currQ === 'major' || currQ === 'minor') {
                // Check if current is Tonic?
                // Even if not strict Tonic of song, V->I relationship holds locally.
                return 'perfect';
            }
        }
    }

    // --- PLAGAL CADENCE (IV -> I) ---
    // Root Movement: Up 5th (7 semitones) or Down 4th
    // F (5) -> C (0) = 7 semitones
    if (interval === 7) {
        if (prevQ === 'major' || prevQ === 'minor') { // minor plagal exists (iv-I)
             if (currQ === 'major' || currQ === 'minor') return 'plagal';
        }
    }

    // --- DECEPTIVE CADENCE (V -> vi) ---
    // Root Movement: Up Major 2nd (2 semitones) for Major Key (V -> vi)
    // G (7) -> A (9) = 2 semitones
    // Root Movement: Up Minor 2nd (1 semitone) for Minor Key (V -> VI)
    // G (7) -> Ab (8) = 1 semitone
    if (prevQ === 'major' || prevQ === 'dom7') {
        if (interval === 2 && currQ === 'minor') return 'deceptive';
        if (interval === 1 && currQ === 'major') return 'deceptive';
    }

    // --- HALF CADENCE (-> V) ---
    // Usually ends on V.
    // We need to know if 'currChord' is V of the key.
    if (currChord.key) {
        const keyRootIdx = NOTES.indexOf(normalizeKey(currChord.key));
        if (keyRootIdx !== -1) {
            const dominantPC = (keyRootIdx + 7) % 12;
            if (currChord.rootPitchClass === dominantPC) {
                // It ends on Dominant.
                // Could be I->V (Half) or IV->V or ii->V
                if (currQ === 'major' || currQ === 'dom7') {
                    // Check prev chord to ensure it's not V->V (Static)
                    if (prevChord.rootPitchClass !== currChord.rootPitchClass) {
                        return 'half';
                    }
                }
            }
        }
    }

    // --- PHRYGIAN HALF CADENCE (iv6 -> V) ---
    // iv -> V in minor.
    // Fm (5) -> G (7). Interval 2.
    if (interval === 2 && prevQ === 'minor' && (currQ === 'major' || currQ === 'dom7')) {
        return 'half_phrygian';
    }

    return 'none';
};
